import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { SearchSelect } from '../components/SearchSelect';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Plus, Search, Filter, Ticket, AlertCircle, CheckCircle2, Clock, 
  User, Calendar, MessageSquare, Trash2, Edit, ChevronRight,
  AlertTriangle, CircleDot, Flag, Phone, MapPin
} from 'lucide-react';

const priorityConfig = {
  low: { label: 'Laag', color: 'bg-slate-500', textColor: 'text-slate-500', borderColor: 'border-slate-500' },
  normal: { label: 'Normaal', color: 'bg-blue-500', textColor: 'text-blue-500', borderColor: 'border-blue-500' },
  high: { label: 'Hoog', color: 'bg-orange-500', textColor: 'text-orange-500', borderColor: 'border-orange-500' },
  urgent: { label: 'Urgent', color: 'bg-red-500', textColor: 'text-red-500', borderColor: 'border-red-500' }
};

const statusConfig = {
  open: { label: 'Open', color: 'bg-yellow-500', icon: CircleDot },
  in_progress: { label: 'In behandeling', color: 'bg-blue-500', icon: Clock },
  closed: { label: 'Afgehandeld', color: 'bg-green-500', icon: CheckCircle2 }
};

const categoryConfig = {
  problem: { label: 'Probleem', icon: AlertCircle },
  question: { label: 'Vraag', icon: MessageSquare },
  memo: { label: 'Memo', icon: Flag },
  task: { label: 'Taak', icon: CheckCircle2 },
  other: { label: 'Anders', icon: Ticket }
};

export default function Tickets() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('active');
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const tid = searchParams.get('ticket');
    if (tid && tickets.length) {
      const t = tickets.find(x => x.id === tid);
      if (t) {
        setSelectedTicket(t);
        setDetailDialogOpen(true);
      }
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets]);
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [stats, setStats] = useState({ open: 0, in_progress: 0, closed: 0, total: 0 });
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketCustomer, setTicketCustomer] = useState(null);

  useEffect(() => {
    const cid = selectedTicket?.customer_id;
    if (!cid) { setTicketCustomer(null); return; }
    let active = true;
    (async () => {
      try {
        const res = await api.get(`/customers/${cid}`);
        let cust = res.data;
        if (!cust.street && !cust.city) {
          try {
            const pr = await api.get('/properties', { params: { customer_id: cid } });
            const p = pr.data?.[0];
            if (p) cust = { ...cust, street: p.street, postal_code: p.postal_code, city: p.city };
          } catch (e) { /* geen property */ }
        }
        if (active) setTicketCustomer(cust);
      } catch (e) {
        if (active) setTicketCustomer(null);
      }
    })();
    return () => { active = false; };
  }, [selectedTicket?.customer_id]);
  const [newComment, setNewComment] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'normal',
    category: 'task',
    customer_id: '',
    assigned_to: [],
    deadline: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [ticketsRes, customersRes, usersRes, statsRes] = await Promise.all([
        api.get('/tickets'),
        api.get('/customers'),
        api.get('/users'),
        api.get('/tickets/stats/summary')
      ]);
      setTickets(ticketsRes.data);
      setCustomers(customersRes.data);
      setUsers(usersRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Kon tickets niet laden');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.error('Titel is verplicht');
      return;
    }

    try {
      const payload = {
        ...formData,
        customer_id: formData.customer_id || null,
        assigned_to: formData.assigned_to,
        deadline: formData.deadline || null
      };

      if (editingTicket) {
        await api.put(`/tickets/${editingTicket.id}`, payload);
        toast.success('Ticket bijgewerkt');
      } else {
        await api.post('/tickets', payload);
        toast.success('Ticket aangemaakt');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Kon ticket niet opslaan');
    }
  };

  const handleDelete = async (ticketId) => {
    if (!window.confirm('Weet je zeker dat je dit ticket wilt verwijderen?')) return;
    
    try {
      await api.delete(`/tickets/${ticketId}`);
      toast.success('Ticket verwijderd');
      setDetailDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Kon ticket niet verwijderen');
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      await api.put(`/tickets/${ticketId}`, { status: newStatus });
      toast.success('Status bijgewerkt');
      fetchData();
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      toast.error('Kon status niet bijwerken');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedTicket) return;
    
    try {
      const res = await api.post(`/tickets/${selectedTicket.id}/comments`, { content: newComment });
      setSelectedTicket(res.data);
      setNewComment('');
      toast.success('Notitie toegevoegd');
      fetchData();
    } catch (error) {
      toast.error('Kon notitie niet toevoegen');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!selectedTicket) return;
    
    try {
      await api.delete(`/tickets/${selectedTicket.id}/comments/${commentId}`);
      setSelectedTicket(prev => ({
        ...prev,
        comments: prev.comments.filter(c => c.id !== commentId)
      }));
      toast.success('Notitie verwijderd');
      fetchData();
    } catch (error) {
      toast.error('Kon notitie niet verwijderen');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      priority: 'normal',
      category: 'task',
      customer_id: '',
      assigned_to: [],
      deadline: ''
    });
    setEditingTicket(null);
  };

  const openEditDialog = (ticket) => {
    setEditingTicket(ticket);
    setFormData({
      title: ticket.title,
      description: ticket.description || '',
      priority: ticket.priority,
      category: ticket.category,
      customer_id: ticket.customer_id || '',
      assigned_to: Array.isArray(ticket.assigned_to) ? ticket.assigned_to : (ticket.assigned_to ? [ticket.assigned_to] : []),
      deadline: ticket.deadline ? ticket.deadline.split('T')[0] : ''
    });
    setDialogOpen(true);
  };

  const openDetailDialog = (ticket) => {
    setSelectedTicket(ticket);
    setDetailDialogOpen(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('nl-BE', { 
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const isOverdue = (deadline) => {
    if (!deadline) return false;
    return new Date(deadline) < new Date();
  };

  // Filter tickets
  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = ticket.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (ticket.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (ticket.customer_name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all'
      || (filterStatus === 'active' ? ticket.status !== 'closed' : ticket.status === filterStatus);
    const matchesPriority = filterPriority === 'all' || ticket.priority === filterPriority;
    const matchesCategory = filterCategory === 'all' || ticket.category === filterCategory;
    return matchesSearch && matchesStatus && matchesPriority && matchesCategory;
  });

  // Eigen tickets van de ingelogde gebruiker altijd bovenaan
  const myTickets = filteredTickets.filter(t => t.assigned_to && t.assigned_to === user?.id);
  const otherTickets = filteredTickets.filter(t => !t.assigned_to || t.assigned_to !== user?.id);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tickets-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Ticket className="w-6 h-6" />
            Tickets
          </h1>
          <p className="text-muted-foreground text-sm">Beheer problemen, vragen, memo's en taken</p>
        </div>
        <Button onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="new-ticket-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nieuw Ticket
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="cursor-pointer hover:bg-muted/50" onClick={() => setFilterStatus('open')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Open</p>
                <p className="text-2xl font-bold text-yellow-500">{stats.open}</p>
              </div>
              <CircleDot className="w-8 h-8 text-yellow-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:bg-muted/50" onClick={() => setFilterStatus('in_progress')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">In behandeling</p>
                <p className="text-2xl font-bold text-blue-500">{stats.in_progress}</p>
              </div>
              <Clock className="w-8 h-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:bg-muted/50" onClick={() => setFilterStatus('closed')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Afgehandeld</p>
                <p className="text-2xl font-bold text-green-500">{stats.closed}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:bg-muted/50" onClick={() => setFilterStatus('all')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Totaal</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <Ticket className="w-8 h-8 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            placeholder="Zoeken..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-tickets"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Actieve tickets</SelectItem>
            <SelectItem value="all">Alle statussen</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="in_progress">In behandeling</SelectItem>
            <SelectItem value="closed">Afgehandeld</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterPriority} onValueChange={setFilterPriority}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Prioriteit" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle prioriteiten</SelectItem>
            <SelectItem value="urgent">Urgent</SelectItem>
            <SelectItem value="high">Hoog</SelectItem>
            <SelectItem value="normal">Normaal</SelectItem>
            <SelectItem value="low">Laag</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Categorie" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle categorieën</SelectItem>
            <SelectItem value="problem">Probleem</SelectItem>
            <SelectItem value="question">Vraag</SelectItem>
            <SelectItem value="memo">Memo</SelectItem>
            <SelectItem value="task">Taak</SelectItem>
            <SelectItem value="other">Anders</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tickets List */}
      <div className="space-y-3">
        {filteredTickets.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              <Ticket className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Geen tickets gevonden</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {myTickets.length > 0 && (
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide pt-1" data-testid="my-tickets-header">
                Mijn tickets ({myTickets.length})
              </h3>
            )}
            {[...myTickets, ...otherTickets].map((ticket, idx) => {
            const showOtherHeader = myTickets.length > 0 && idx === myTickets.length;
            const StatusIcon = statusConfig[ticket.status]?.icon || CircleDot;
            const CategoryIcon = categoryConfig[ticket.category]?.icon || Ticket;
            const overdue = ticket.status !== 'closed' && isOverdue(ticket.deadline);
            
            return (
              <div key={ticket.id}>
              {showOtherHeader && (
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide pt-3 pb-3" data-testid="other-tickets-header">
                  Overige tickets ({otherTickets.length})
                </h3>
              )}
              <Card 
                className={`cursor-pointer hover:bg-muted/50 transition-colors ${overdue ? 'border-red-500/50' : ''}`}
                onClick={() => openDetailDialog(ticket)}
                data-testid={`ticket-${ticket.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Priority indicator */}
                    <div className={`w-1 h-full min-h-[60px] rounded-full ${priorityConfig[ticket.priority]?.color}`} />
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-medium truncate">{ticket.title}</h3>
                            <Badge variant="outline" className={`${priorityConfig[ticket.priority]?.borderColor} ${priorityConfig[ticket.priority]?.textColor}`}>
                              {priorityConfig[ticket.priority]?.label}
                            </Badge>
                            <Badge variant="secondary" className="flex items-center gap-1">
                              <CategoryIcon className="w-3 h-3" />
                              {categoryConfig[ticket.category]?.label}
                            </Badge>
                          </div>
                          {ticket.description && (
                            <p className="text-sm text-muted-foreground mt-1 line-clamp-1">{ticket.description}</p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground flex-wrap">
                            {ticket.customer_name && (
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {ticket.customer_name}
                              </span>
                            )}
                            {ticket.created_by_name && (
                              <span className="flex items-center gap-1 text-blue-500">
                                <User className="w-3 h-3" />
                                Aangemaakt: {ticket.created_by_name}
                              </span>
                            )}
                            {ticket.assigned_to_name && (
                              <span className="flex items-center gap-1 text-green-500">
                                <User className="w-3 h-3" />
                                Toegewezen: {ticket.assigned_to_name}
                              </span>
                            )}
                            {ticket.deadline && (
                              <span className={`flex items-center gap-1 ${overdue ? 'text-red-500' : ''}`}>
                                <Calendar className="w-3 h-3" />
                                {formatDate(ticket.deadline)}
                                {overdue && <AlertTriangle className="w-3 h-3" />}
                              </span>
                            )}
                            {ticket.comments?.length > 0 && (
                              <span className="flex items-center gap-1">
                                <MessageSquare className="w-3 h-3" />
                                {ticket.comments.length}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={`${statusConfig[ticket.status]?.color} text-white`}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {statusConfig[ticket.status]?.label}
                          </Badge>
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              </div>
            );
          })}
          </>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingTicket ? 'Ticket bewerken' : 'Nieuw Ticket'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Titel *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Korte beschrijving van het ticket"
                data-testid="ticket-title-input"
              />
            </div>
            <div>
              <Label>Beschrijving</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Uitgebreide beschrijving..."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prioriteit</Label>
                <Select value={formData.priority} onValueChange={(v) => setFormData(prev => ({ ...prev, priority: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Laag</SelectItem>
                    <SelectItem value="normal">Normaal</SelectItem>
                    <SelectItem value="high">Hoog</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Categorie</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="problem">Probleem</SelectItem>
                    <SelectItem value="question">Vraag</SelectItem>
                    <SelectItem value="memo">Memo</SelectItem>
                    <SelectItem value="task">Taak</SelectItem>
                    <SelectItem value="other">Anders</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Klant (optioneel)</Label>
              <SearchSelect
                options={[...customers]
                  .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`, 'nl'))
                  .map(c => ({
                    value: c.id,
                    label: c.company_name
                      ? `${c.company_name} — ${c.first_name} ${c.last_name}`
                      : `${c.first_name} ${c.last_name}`,
                  }))}
                value={formData.customer_id}
                onChange={(id) => setFormData(prev => ({ ...prev, customer_id: id || '' }))}
                placeholder="Zoek klant op naam of bedrijf..."
                autoOpen
                className="mt-1"
              />
            </div>
            <div>
              <Label>Toegewezen aan (meerdere mogelijk)</Label>
              <div className="border rounded-md max-h-40 overflow-y-auto p-2 space-y-0.5 mt-1" data-testid="ticket-assignees-list">
                {[...users]
                  .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`, 'nl'))
                  .map(u => (
                  <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/50 rounded px-2 py-1.5">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-primary"
                      checked={formData.assigned_to.includes(u.id)}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        assigned_to: e.target.checked
                          ? [...prev.assigned_to, u.id]
                          : prev.assigned_to.filter(id => id !== u.id)
                      }))}
                      data-testid={`ticket-assignee-${u.id}`}
                    />
                    {u.first_name} {u.last_name}
                  </label>
                ))}
              </div>
              {formData.assigned_to.length > 0 && (
                <p className="text-xs text-muted-foreground mt-1" data-testid="ticket-assignees-count">
                  {formData.assigned_to.length} geselecteerd
                </p>
              )}
            </div>
            <div>
              <Label>Deadline (optioneel)</Label>
              <Input
                type="date"
                value={formData.deadline}
                onChange={(e) => setFormData(prev => ({ ...prev, deadline: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuleren</Button>
            <Button onClick={handleSubmit} data-testid="save-ticket-btn">
              {editingTicket ? 'Opslaan' : 'Aanmaken'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedTicket && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <DialogTitle className="flex items-center gap-2">
                      {selectedTicket.title}
                    </DialogTitle>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge className={`${statusConfig[selectedTicket.status]?.color} text-white`}>
                        {statusConfig[selectedTicket.status]?.label}
                      </Badge>
                      <Badge variant="outline" className={`${priorityConfig[selectedTicket.priority]?.borderColor} ${priorityConfig[selectedTicket.priority]?.textColor}`}>
                        {priorityConfig[selectedTicket.priority]?.label}
                      </Badge>
                      <Badge variant="secondary">
                        {categoryConfig[selectedTicket.category]?.label}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => { openEditDialog(selectedTicket); setDetailDialogOpen(false); }}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => handleDelete(selectedTicket.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                {/* Status Change */}
                <div className="flex items-center gap-2">
                  <Label className="w-20">Status:</Label>
                  <div className="flex gap-2">
                    {Object.entries(statusConfig).map(([key, config]) => (
                      <Button
                        key={key}
                        variant={selectedTicket.status === key ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => handleStatusChange(selectedTicket.id, key)}
                        className={selectedTicket.status === key ? config.color : ''}
                      >
                        {config.label}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Description */}
                {selectedTicket.description && (
                  <div>
                    <Label className="text-muted-foreground">Beschrijving</Label>
                    <p className="mt-1 text-sm whitespace-pre-wrap">{selectedTicket.description}</p>
                  </div>
                )}

                {/* Meta info */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {selectedTicket.customer_name && (
                    <div>
                      <Label className="text-muted-foreground">Klant</Label>
                      <p className="flex items-center gap-1 mt-1">
                        <User className="w-4 h-4" />
                        {selectedTicket.customer_name}
                      </p>
                      {ticketCustomer?.phone && (
                        <a href={`tel:${ticketCustomer.phone.replace(/\s/g, '')}`}
                          className="flex items-center gap-1 mt-1 text-primary hover:underline"
                          data-testid="ticket-customer-phone">
                          <Phone className="w-4 h-4" />
                          {ticketCustomer.phone}
                        </a>
                      )}
                      {ticketCustomer && (ticketCustomer.street || ticketCustomer.city) && (
                        <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([ticketCustomer.street, ticketCustomer.postal_code, ticketCustomer.city].filter(Boolean).join(' '))}`}
                          target="_blank" rel="noopener noreferrer"
                          className="flex items-start gap-1 mt-1 text-primary hover:underline"
                          data-testid="ticket-customer-address">
                          <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <span>{[ticketCustomer.street, [ticketCustomer.postal_code, ticketCustomer.city].filter(Boolean).join(' ')].filter(Boolean).join(', ')}</span>
                        </a>
                      )}
                    </div>
                  )}
                  {selectedTicket.assigned_to_name && (
                    <div>
                      <Label className="text-muted-foreground">Toegewezen aan</Label>
                      <p className="mt-1">{selectedTicket.assigned_to_name}</p>
                    </div>
                  )}
                  {selectedTicket.deadline && (
                    <div>
                      <Label className="text-muted-foreground">Deadline</Label>
                      <p className={`flex items-center gap-1 mt-1 ${isOverdue(selectedTicket.deadline) && selectedTicket.status !== 'closed' ? 'text-red-500' : ''}`}>
                        <Calendar className="w-4 h-4" />
                        {formatDate(selectedTicket.deadline)}
                        {isOverdue(selectedTicket.deadline) && selectedTicket.status !== 'closed' && (
                          <Badge variant="destructive" className="ml-2">Verlopen</Badge>
                        )}
                      </p>
                    </div>
                  )}
                  <div>
                    <Label className="text-muted-foreground">Aangemaakt door</Label>
                    <p className="mt-1">{selectedTicket.created_by_name}</p>
                    <p className="text-xs text-muted-foreground">{formatDateTime(selectedTicket.created_at)}</p>
                  </div>
                </div>

                {/* Comments/Notes */}
                <div className="border-t pt-4">
                  <Label className="text-muted-foreground">Notities ({selectedTicket.comments?.length || 0})</Label>
                  
                  {/* Add comment */}
                  <div className="flex gap-2 mt-2">
                    <Textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Voeg een notitie toe..."
                      rows={2}
                      className="flex-1"
                    />
                    <Button onClick={handleAddComment} disabled={!newComment.trim()}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Comments list */}
                  <div className="space-y-3 mt-4">
                    {selectedTicket.comments?.map((comment) => (
                      <div key={comment.id} className="bg-muted/50 p-3 rounded-lg">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="text-sm whitespace-pre-wrap">{comment.content}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {comment.created_by_name} - {formatDateTime(comment.created_at)}
                            </p>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDeleteComment(comment.id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
